import React, { useMemo } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../../utils/fontawesome';


const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const normalizeDate = (value) => {
  if (!value) return null;
  
  let dateObj;

  if (value instanceof Date) {
    dateObj = new Date(value);
  } else if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // Gestion locale d'une date sans heure pour éviter les décalages de fuseau
    const [y, m, d] = value.split("-").map(Number);
    dateObj = new Date(y, m - 1, d, 0, 0, 0, 0);
  } else {
    dateObj = new Date(value);
  }

  if (Number.isNaN(dateObj.getTime())) return null;
  dateObj.setHours(0, 0, 0, 0); // n
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  
  return {
    dateObj,
    iso: `${year}-${month}-${day}`,
  };
};

const toQuantity = (value, fallback = 1) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  return fallback;
};

const buildCalendarDays = (monthRef, blockages = [], evenements = [], reservations = []) => {
  if (!monthRef) return [];

  const year = monthRef.getFullYear();
  const month = monthRef.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const offset = (firstDay.getDay() + 6) % 7; // start week on Monday
  const totalDays = lastDay.getDate();
  const cells = offset + totalDays;
  const normalizedBlockages = (Array.isArray(blockages) ? blockages : []).map(
    (item, index) => {
      const start = normalizeDate(
        item.date_debut || item.start || item.debut || item.date
      );
      const end = normalizeDate(item.date_fin || item.fin || item.end || item.date);
      return {
        type: item.type || item.nature || "blocage",
        zone: item.zone_nom || item.zone || item.zoneLabel || null,
        atelier: item.atelier_nom || item.atelier || item.atelierLabel || null,
        start,
        end,
        raw: item,
        isEvent: false,
        quantity: toQuantity(
          item.quantite || item.nombre || item.count || item.nombre_blocage || 1
        ),
      };
    }
  );

  const normalizedEvenements = (Array.isArray(evenements) ? evenements : []).map(
    (item, index) => {
      const start = normalizeDate(item.date_debut || item.start || item.debut || item.date);
      const end = normalizeDate(item.date_fin || item.fin || item.end || item.date);
      return {
        type: item.nom || "Événement",
        start,
        end,
        raw: item,
        isEvent: true,
        quantity: toQuantity(item.quantite || item.nombre || item.count || 1),
      };
    }
  );
  const normalizedReservations = (Array.isArray(reservations) ? reservations : []).map(
    (item, index) => {
      const start = normalizeDate(
        item.date ||
          item.date_atelier ||
          item.jour ||
          item.date_debut ||
          item.start ||
          item.debut
      );
      const end = normalizeDate(item.date_fin || item.fin || item.end || item.date);
      return {
        type: item.type  || item.status || "reservation",
        start,
        end: end || start,
        raw: item,
        nombre_reservation: item.nombre_reservation,
        quantity: toQuantity(
          item.nombre_reservation ||
            item.nombre ||
            item.quantite ||
            item.count ||
            1
        ),
        isReservation: true,
      };
    }
  );

  return Array.from({ length: cells }, (_, idx) => {
    if (idx < offset) return { iso: null, label: "", blockages: [] };

    const dayNumber = idx - offset + 1;
    const dateObj = new Date(year, month, dayNumber);
    const isoDate = normalizeDate(dateObj)?.iso;

    const activeBlockages = normalizedBlockages.filter((bloc) => {
      if (!bloc.start || !bloc.start.dateObj || !bloc.end || !bloc.end.dateObj)
        return false;
      const targetTime = dateObj.getTime();
      return (
        targetTime >= bloc.start.dateObj.getTime() &&
        targetTime <= bloc.end.dateObj.getTime()
      );
    });
    const activeEvents = normalizedEvenements.filter((evt) => {
      if (!evt.start || !evt.start.dateObj || !evt.end || !evt.end.dateObj)
        return false;
      const targetTime = dateObj.getTime();
      return (
        targetTime >= evt.start.dateObj.getTime() &&
        targetTime <= evt.end.dateObj.getTime()
      );
    });

    const activeReservations = normalizedReservations.filter((resa) => {
        if (!resa.start || !resa.start.dateObj || !resa.end || !resa.end.dateObj)
          return false;
        const targetTime = dateObj.getTime();
        return (
          targetTime >= resa.start.dateObj.getTime() &&
          targetTime <= resa.end.dateObj.getTime()
        );
      });


    return {
      iso: isoDate,
      label: String(dayNumber),
      blockages: activeBlockages,
      events: activeEvents,
      reservations: activeReservations
    };
  });
};
const typeToBadgeClass = (typeLabel = "", isEvent = false) => {
  const label =
    typeof typeLabel === "string" ? typeLabel.toLowerCase() : String(typeLabel || "").toLowerCase();

  if (isEvent) {
    return "bg-green-100 text-green-800 border-green-300";
  }
  if (label.includes("reservation")) {
    return "bg-sky-100 text-sky-800 border-sky-300";
  }
  const normalized = label;
  if (normalized.includes("blocage")) {
    return "bg-rose-100 text-rose-800 border-rose-300";
  }
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const GestionCalendrier = ({
  month,
  onMonthChange,
  blockages = [],
  events = [],
  reservations = [],
  selectedDay,
  onSelectDay,
}) => {
  const calendarDays = useMemo(
    () => buildCalendarDays(month || new Date(), blockages, events, reservations),
    [month, blockages, events, reservations]
  );

  const monthLabel = (month || new Date()).toLocaleString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  const handleMonthChange = (direction) => {
    if (!onMonthChange) return;
    const current = month || new Date();
    const next = new Date(current.getFullYear(), current.getMonth() + direction, 1);
    onMonthChange(next);
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 flex flex-col gap-4 h-full">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#F36D26]">
            {monthLabel}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleMonthChange(-1)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:border-slate-400"
          >
            {"<"}
          </button>
          <button
            type="button"
            onClick={() => onMonthChange && onMonthChange(new Date())}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:border-slate-400"
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={() => handleMonthChange(1)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:border-slate-400"
          >
            {">"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">
        {DAY_LABELS.map((label) => (
          <div key={label} className="py-2">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {calendarDays.map((day, index) => {
          const isSelected = day.iso && selectedDay === day.iso;

          const blocageCount = Array.isArray(day.blockages)
            ? day.blockages.reduce(
                (total, item) => total + toQuantity(item?.quantity, 1),
                0
              )
            : 0;
          const eventCount = Array.isArray(day.events)
            ? day.events.reduce(
                (total, item) => total + toQuantity(item?.quantity, 1),
                0
              )
            : 0;
          const reservationCount = Array.isArray(day.reservations)
            ? day.reservations.reduce(
                (total, item) => total + toQuantity(item?.quantity, 1),
                0
              )
            : 0;

          return (
            <button
              key={day.iso || `empty-${index}`}
              type="button"
              onClick={() => day.iso && onSelectDay && onSelectDay(day.iso)}
              className={`min-h-[100px] rounded-lg border p-2 text-left flex flex-col gap-2 transition-colors ${
                day.iso
                  ? isSelected
                    ? "border-[#F36D26] bg-orange-50"
                    : "border-slate-200 hover:border-slate-400"
                  : "border-transparent bg-transparent cursor-default"
              }`}
              disabled={!day.iso}
            >
              <span className="text-sm font-semibold text-slate-700">
                {day.label}
              </span>

              <div className="flex flex-col gap-1">
                {blocageCount > 0 && (
                  <span
                    className={`text-[11px] px-2 py-1 rounded-md border ${typeToBadgeClass(
                      "blocage",
                      false
                    )} inline-flex`}
                  >
                    blocage{blocageCount > 1 ? "s" : ""}
                  </span>
                )}

                {eventCount > 0 && (
                  <span
                    className={`text-[11px] px-2 py-1 rounded-md border ${typeToBadgeClass(
                      "evenement",
                      true
                    )} inline-flex`}
                  >
                    {eventCount} événement{eventCount > 1 ? "s" : ""}
                  </span>
                )}

                {reservationCount > 0 && (
                  <span
                    className={`text-[11px] px-2 py-1 rounded-md border ${typeToBadgeClass(
                      "reservation",
                      false
                    )} inline-flex`}
                  >
                    {reservationCount} réservation{reservationCount > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default GestionCalendrier;
